import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useQuery } from "react-query";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";

import {
  useAppProperties,
  useNotificationPreferences,
  useUpdateNotificationPreferences,
} from "@/hooks";
import { useAmityAuth, useUser } from "@/providers/context";
import { slugify } from "@/utils";
import { CommunityGroup, getCommunityGroups } from "@/api";

import { CommunityGroupWithDetails } from "@/app/modules/Community/types";
import {
  getComment,
  getCommunities,
  getCommunityById,
} from "@/app/modules/Community/util";

interface IndexedGroups {
  [key: string]: CommunityGroupWithDetails;
}

interface Groups {
  joined: CommunityGroupWithDetails[];
  not_joined: CommunityGroupWithDetails[];
  groups_by_slug: IndexedGroups;
}

interface ContextValue {
  groups: Groups | undefined;
  isError: boolean;
  activeGroup: CommunityGroupWithDetails | undefined;
}

interface ProviderProps {
  children: ReactNode | ReactNode[];
}

const getGroupWithDetails = (
  group: CommunityGroup | undefined,
  community: Amity.Community | undefined,
): CommunityGroupWithDetails | undefined => {
  if (!group || !community) {
    return undefined;
  }

  return {
    ...group,
    membersCount: community.membersCount,
    isJoined: !!community.isJoined,
  };
};

type OptionalContextValue = ContextValue | undefined;
const CommunityGroupsContext = createContext<OptionalContextValue>(undefined);

const CommunityGroupsProvider = ({ children }: ProviderProps) => {
  const navigate = useNavigate();
  const { uid } = useUser();
  const { communityGroupsEnabled } = useAppProperties();
  const { data: notificationPreferences } = useNotificationPreferences();
  const updateNotificationPreferences = useUpdateNotificationPreferences();
  const lastUpdatedGroupsRef = useRef<string[]>();

  const { authState } = useAmityAuth();

  const enabled =
    authState.state === "success" && !!authState.authenticatedUser && !!uid;

  const getGroups = useCallback(async (): Promise<Groups | undefined> => {
    const groups = await getCommunityGroups(); // Groups from VidApp groups table

    // For clarity, we'll use the term "group" to refer to a group from VidApp DB
    // and "community" to refer to a community from Amity
    // data from both will be combined and returned as CommunityGroupWithDetails
    //
    // VidApp groups table is used as source of truth
    // But we need to get the community data from Amity to get member count and the logged in user's joined/not joined status

    if (!groups) {
      return { joined: [], not_joined: [], groups_by_slug: {} };
    }

    if (!communityGroupsEnabled) {
      const mainGroup = groups?.find((g) => g.Type === "main");
      const mainCommunity = mainGroup
        ? await getCommunityById(mainGroup.CommunityID)
        : undefined;

      const mainGroupWithDetails = getGroupWithDetails(
        mainGroup,
        mainCommunity,
      );

      if (!mainGroupWithDetails || !mainCommunity) {
        return { joined: [], not_joined: [], groups_by_slug: {} };
      }

      return {
        joined: [mainGroupWithDetails],
        not_joined: [],
        groups_by_slug: {
          [slugify(mainCommunity.displayName)]: mainGroupWithDetails,
        },
      };
    }

    const allCommunities = await getCommunities();

    const { joined, notJoined } = groups.reduce(
      (acc, group) => {
        const community = allCommunities?.find(
          (c) => c.communityId === group.CommunityID,
        );

        const communityWithDetails = getGroupWithDetails(group, community);

        if (!communityWithDetails || !community) {
          return acc;
        }

        if (community.isJoined) {
          acc.joined.push(communityWithDetails);
        } else {
          acc.notJoined.push(communityWithDetails);
        }
        return acc;
      },
      {
        joined: [] as CommunityGroupWithDetails[],
        notJoined: [] as CommunityGroupWithDetails[],
      },
    );

    const combined = [...joined, ...notJoined];

    const GROUPS_BY_SLUG: IndexedGroups = {};

    for (const group of combined) {
      GROUPS_BY_SLUG[slugify(group.Name)] = group;
    }

    return {
      joined: joined.sort((a, b) => a.Position - b.Position),
      not_joined: notJoined.sort((a, b) => a.Position - b.Position),
      groups_by_slug: GROUPS_BY_SLUG,
    };
  }, [communityGroupsEnabled]);

  const query = useQuery(["community_groups", uid], () => getGroups(), {
    enabled,
  });

  const { group } = useParams();

  const [searchParams] = useSearchParams();
  const queryParameters = useMemo(
    () => new URLSearchParams(searchParams),
    [searchParams],
  );

  const activeGroup = useMemo(() => {
    if (group && query.data?.groups_by_slug) {
      return query.data.groups_by_slug[group];
    }

    return undefined;
  }, [group, query.data]);

  // If we're coming from a notification card, we need to use community_id & comment_id or post_id params to redirect
  useEffect(() => {
    const navigateToPostFromCommentId = async (
      groupSlug: string,
      commentId: string,
    ) => {
      try {
        const comment = await getComment(commentId);
        navigate(`/community/${groupSlug}/${comment?.referenceId}`, {
          replace: true,
        });
      } catch (error) {
        navigate(`/community/${groupSlug}`);
      }
    };

    if (queryParameters && query.data?.groups_by_slug) {
      const community_id = queryParameters.get("community_id");
      const comment_id = queryParameters.get("comment_id");
      const post_id = queryParameters.get("post_id");

      if (!community_id || (!comment_id && !post_id)) {
        return;
      }

      const matchingGroup =
        query.data.joined.find(
          ({ CommunityID }) => CommunityID === community_id,
        ) ??
        query.data.not_joined.find(
          ({ CommunityID }) => CommunityID === community_id,
        );

      if (!matchingGroup) {
        return;
      }

      const groupSlug = slugify(matchingGroup.Name);

      if (comment_id) {
        navigateToPostFromCommentId(groupSlug, comment_id);
      } else {
        navigate(`/community/${groupSlug}/${post_id}`, {
          replace: true,
        });
      }
    }
  }, [queryParameters, query.data, navigate]);

  // When joined groups change we need to update communities in the notification preferences for the user
  useEffect(() => {
    const joinedGroups = query.data?.joined.map(
      ({ CommunityID }) => CommunityID,
    );
    const currentSettings = notificationPreferences?.communities;

    if (!joinedGroups || !currentSettings) {
      return;
    }

    // Check if we've already updated for these groups
    const lastUpdatedGroups = lastUpdatedGroupsRef.current;
    if (
      lastUpdatedGroups &&
      lastUpdatedGroups.length === joinedGroups.length &&
      lastUpdatedGroups.every((id, index) => id === joinedGroups[index])
    ) {
      return;
    }

    // Check if arrays match (ignoring order)
    const joinedGroupsSet = new Set(joinedGroups);
    const currentSettingsSet = new Set(currentSettings);
    const setsMatch =
      joinedGroupsSet.size === currentSettingsSet.size &&
      joinedGroups.every((id) => currentSettingsSet.has(id));

    if (!setsMatch) {
      updateNotificationPreferences.mutate({ communities: joinedGroups });
      lastUpdatedGroupsRef.current = [...joinedGroups];
    }
  }, [
    query.data?.joined,
    notificationPreferences?.communities,
    updateNotificationPreferences,
  ]);

  return (
    <CommunityGroupsContext.Provider
      value={{
        groups: query.data,
        isError: query.isError || !enabled,
        activeGroup,
      }}
    >
      {children}
    </CommunityGroupsContext.Provider>
  );
};

export { CommunityGroupsContext, CommunityGroupsProvider };
